/**
 * ConnectionContextMenu - Context menu for creating nodes from connections
 * 
 * Shows when user drags a connection and releases on empty canvas
 */

import React from 'react';
import IconProvider from '@/components/ui/IconProvider';

interface NodeTypeOption {
  type: string;
  label: string;
  icon: string;
  color: string;
}

const nodeTypeOptions: NodeTypeOption[] = [
  { type: 'dialog', label: 'Dialog', icon: 'fa-comment', color: 'hover:bg-gray-100' },
  { type: 'question', label: 'Question', icon: 'fa-circle-question', color: 'hover:bg-purple-100' },
  { type: 'context', label: 'Context', icon: 'fa-circle-info', color: 'hover:bg-blue-100' },
  { type: 'setter', label: 'Setter', icon: 'fa-gear', color: 'hover:bg-green-100' },
  { type: 'condition', label: 'Condition', icon: 'fa-code-branch', color: 'hover:bg-orange-100' },
  { type: 'external-dialog', label: 'External Dialog', icon: 'fa-arrow-right-to-bracket', color: 'hover:bg-cyan-100' },
  { type: 'end-dialog', label: 'End Dialog', icon: 'fa-circle-stop', color: 'hover:bg-red-100' },
];

interface ConnectionContextMenuProps {
  position: { x: number; y: number };
  onSelect: (nodeType: string) => void;
  onClose: () => void;
}

export function ConnectionContextMenu({ position, onSelect, onClose }: ConnectionContextMenuProps) {
  React.useEffect(() => {
    // Track if the menu is still in its "grace period" where we ignore click-outside events
    let isGracePeriodActive = true;
    
    const handleClickOutside = () => {
      // Ignore clicks during grace period to prevent premature closing
      if (isGracePeriodActive) {
        return;
      }
      
      onClose();
    };
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    // Register event listeners immediately but with grace period protection
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    // End grace period after events from the connection drag have settled
    const gracePeriodTimeout = setTimeout(() => {
      isGracePeriodActive = false;
    }, 150);

    return () => {
      clearTimeout(gracePeriodTimeout);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div
      className="fixed z-[9999] bg-background border-2 border-border rounded-lg shadow-lg py-1 min-w-[180px]"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b text-xs font-semibold text-muted-foreground">
        Create Node
      </div>
      {nodeTypeOptions.map((option) => (
        <button
          key={option.type}
          className={`w-full px-3 py-2 flex items-center gap-2 text-sm transition-colors ${option.color}`}
          onClick={() => {
            onSelect(option.type);
            onClose();
          }}
        >
          <IconProvider iconstring={option.icon} size={14} />
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

export default ConnectionContextMenu;

